import axios from 'axios';
import {
  PlanRequest,
  CreatePlanRequestDto,
  UpdatePlanRequestDto,
  PlanRequestFilters,
  PlanRequestStatus,
  VisitState,
  ClientVisitStatesMap,
  ClientHistoryResponse,
  UpdateClientDataDto
} from '../types/planRequest';

const API_URL = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';

class PlanRequestService {
  private baseUrl = `${API_URL}/api/plan-requests`;

  /**
   * Create axios config with authorization header
   */
  private getAxiosConfig(token?: string) {
    if (!token) {
      return {};
    }
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  }

  // ============ Advisor Functions ============

  /**
   * Create a new plan request
   */
  async createRequest(data: CreatePlanRequestDto, token?: string): Promise<PlanRequest> {
    try {
      const response = await axios.post<PlanRequest>(this.baseUrl, data, this.getAxiosConfig(token));
      return response.data;
    } catch (error: any) {
      console.error('Error creating plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to create plan request');
    }
  }

  /**
   * Get all plan requests for a specific advisor.
   * @param limit - optional row cap (server caps at 5000 regardless).
   * @param lite  - when true, server returns a stripped-down projection optimised for list views
   *               (drops snapshotData / enrichedData / planMetrics / history). Use for tables that
   *               only show top-level scalar fields — payload is ~10–50× smaller.
   */
  async getMyRequests(customerId: string, token?: string, limit?: number, lite?: boolean): Promise<PlanRequest[]> {
    try {
      const params: Record<string, string | number | boolean> = { customerId };
      if (limit) params.limit = limit;
      if (lite) params.lite = 'true';
      const config = {
        ...this.getAxiosConfig(token),
        params
      };
      const response = await axios.get<PlanRequest[]>(this.baseUrl, config);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching plan requests:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch plan requests');
    }
  }

  /**
   * Get plan requests for a specific submission
   */
  async getRequestsBySubmission(submissionId: string, token?: string): Promise<PlanRequest[]> {
    try {
      const response = await axios.get<PlanRequest[]>(`${this.baseUrl}/submission/${submissionId}`, this.getAxiosConfig(token));
      return response.data;
    } catch (error: any) {
      console.error('Error fetching submission plan requests:', error);
      return []; // Return empty array if no requests found
    }
  }

  /**
   * Cancel a plan request
   */
  async cancelRequest(id: string, reason?: string): Promise<void> {
    try {
      await axios.put(`${this.baseUrl}/${id}/cancel`, { reason });
    } catch (error: any) {
      console.error('Error cancelling plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to cancel plan request');
    }
  }

  // ============ Admin Functions ============

  /**
   * Get all plan requests (admin view).
   * @param limit - optional row cap (server caps at 5000 regardless).
   * @param lite  - when true, server returns a stripped-down projection optimised for list views.
   */
  async getAllRequests(token?: string, filters?: PlanRequestFilters, limit?: number, lite?: boolean): Promise<PlanRequest[]> {
    try {
      const params: Record<string, unknown> = { ...(filters || {}) };
      if (limit) params.limit = limit;
      if (lite) params.lite = 'true';
      const config = {
        ...this.getAxiosConfig(token),
        params
      };
      const response = await axios.get<PlanRequest[]>(`${this.baseUrl}/admin`, config);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching all plan requests:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch plan requests');
    }
  }

  /**
   * Get a single plan request by ID
   */
  async getRequestById(id: string, token?: string): Promise<PlanRequest> {
    try {
      const response = await axios.get<PlanRequest>(`${this.baseUrl}/${id}`, this.getAxiosConfig(token));
      return response.data;
    } catch (error: any) {
      console.error('Error fetching plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch plan request');
    }
  }

  /**
   * Get plan request status with live SIPS polling
   * Returns status, queue position, scenario progress, metrics, and presentationUrl
   */
  async getPlanStatus(id: string, token?: string): Promise<PlanRequestStatus> {
    try {
      const response = await axios.get<PlanRequestStatus>(
        `${this.baseUrl}/${id}/status`,
        this.getAxiosConfig(token)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching plan status:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch plan status');
    }
  }

  /**
   * Assign a plan request to a planner
   */
  async assignToPlanner(id: string, plannerId: string): Promise<PlanRequest> {
    try {
      const response = await axios.put<PlanRequest>(`${this.baseUrl}/${id}/assign`, {
        assignedTo: plannerId
      });
      return response.data;
    } catch (error: any) {
      console.error('Error assigning plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to assign plan request');
    }
  }

  /**
   * Update plan request progress
   */
  async updateProgress(id: string, percentage: number, notes?: string): Promise<PlanRequest> {
    try {
      const response = await axios.put<PlanRequest>(`${this.baseUrl}/${id}/progress`, {
        progressPercentage: percentage,
        progressNotes: notes
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating progress:', error);
      throw new Error(error.response?.data?.message || 'Failed to update progress');
    }
  }

  /**
   * Mark a plan request as complete
   */
  async markComplete(id: string, planUrl: string, completionNotes?: string): Promise<PlanRequest> {
    try {
      const response = await axios.put<PlanRequest>(`${this.baseUrl}/${id}/complete`, {
        planUrl,
        completionNotes
      });
      return response.data;
    } catch (error: any) {
      console.error('Error completing plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to complete plan request');
    }
  }

  /**
   * Update plan request details (admin)
   */
  async updateRequest(id: string, data: UpdatePlanRequestDto, token?: string): Promise<PlanRequest> {
    try {
      const response = await axios.put<PlanRequest>(`${this.baseUrl}/${id}`, data, this.getAxiosConfig(token));
      return response.data;
    } catch (error: any) {
      console.error('Error updating plan request:', error);
      throw new Error(error.response?.data?.message || 'Failed to update plan request');
    }
  }

  /**
   * Add internal notes (admin only)
   */
  async updateInternalNotes(id: string, notes: string): Promise<void> {
    try {
      await axios.put(`${this.baseUrl}/${id}/notes`, { internalNotes: notes });
    } catch (error: any) {
      console.error('Error updating internal notes:', error);
      throw new Error(error.response?.data?.message || 'Failed to update notes');
    }
  }

  // ============ Client Management Functions ============

  /**
   * Get full client details with all plans
   */
  async getClientDetails(clientId: string, token?: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/client/${clientId}`,
        this.getAxiosConfig(token)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching client details:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch client details');
    }
  }

  /**
   * Update client information
   */
  async updateClient(clientId: string, data: any, token?: string): Promise<void> {
    try {
      await axios.put(
        `${this.baseUrl}/client/${clientId}/update`,
        data,
        this.getAxiosConfig(token)
      );
    } catch (error: any) {
      console.error('Error updating client:', error);
      throw new Error(error.response?.data?.message || 'Failed to update client');
    }
  }

  /**
   * Persist inline Client Details edits onto a single plan. Writes to the
   * plan's origin store, cascades identity across the client's plans, and
   * clears estimate provenance for overridden fields. Returns the updated plan.
   */
  async updateClientData(planId: string, data: UpdateClientDataDto, token?: string): Promise<PlanRequest> {
    try {
      const response = await axios.patch<PlanRequest>(
        `${this.baseUrl}/${planId}/client-data`,
        data,
        this.getAxiosConfig(token)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error saving client data:', error);
      throw new Error(error.response?.data?.message || 'Failed to save client details');
    }
  }

  /**
   * Hide/unhide a client (soft delete)
   */
  async hideClient(clientId: string, hidden: boolean, token?: string): Promise<void> {
    try {
      await axios.put(
        `${this.baseUrl}/client/${clientId}/hide`,
        { hidden },
        this.getAxiosConfig(token)
      );
    } catch (error: any) {
      console.error('Error hiding client:', error);
      throw new Error(error.response?.data?.message || 'Failed to hide client');
    }
  }

  /**
   * Create a revision of an existing plan
   */
  async createRevision(planId: string, data: any, token?: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/${planId}/request-revision`,
        data,
        this.getAxiosConfig(token)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating revision:', error);
      throw new Error(error.response?.data?.message || 'Failed to create revision');
    }
  }

  // ============ Client Workspace ============

  /**
   * "What changed since your last visit" for the dashboard tab. Returns
   * { lastVisitedAt, isFirstVisit, changes[] }. First visit returns empty
   * changes so the UI can show a welcome state.
   */
  async getVisitState(clientId: string, token?: string): Promise<VisitState> {
    const response = await axios.get<VisitState>(
      `${API_URL}/api/clients/${clientId}/visit-state`,
      this.getAxiosConfig(token)
    );
    return response.data;
  }

  /**
   * Bulk "needs rerun?" status for every client the advisor has a visit
   * baseline for. Powers the status light on the My Clients list (one call
   * instead of per-client visit-state requests). Read-only — does not stamp
   * visits. Returns {} on error so the list still renders (statuses default to
   * up-to-date).
   */
  async getVisitStates(token?: string): Promise<ClientVisitStatesMap> {
    try {
      const response = await axios.get<ClientVisitStatesMap>(
        `${API_URL}/api/clients/visit-states`,
        this.getAxiosConfig(token)
      );
      return response.data || {};
    } catch (error) {
      console.warn('Failed to fetch bulk visit states:', error);
      return {};
    }
  }

  /**
   * Stamp the advisor's visit. Called after the dashboard tab renders the
   * what-changed panel so the next visit shows what changed between then
   * and now.
   */
  async markVisited(clientId: string, token?: string): Promise<void> {
    await axios.post(
      `${API_URL}/api/clients/${clientId}/mark-visited`,
      {},
      this.getAxiosConfig(token)
    );
  }

  /**
   * Clear the stored visit-state baseline. Next call to getVisitState
   * returns isFirstVisit=true with no changes. Used when the stored shape
   * is stale and producing a noisy diff.
   */
  async resetVisitState(clientId: string, token?: string): Promise<void> {
    await axios.delete(
      `${API_URL}/api/clients/${clientId}/visit-state`,
      this.getAxiosConfig(token)
    );
  }

  /**
   * Aggregated history across every PlanRequest for the client, newest first.
   */
  async getClientHistory(clientId: string, token?: string): Promise<ClientHistoryResponse> {
    const response = await axios.get<ClientHistoryResponse>(
      `${API_URL}/api/clients/${clientId}/history`,
      this.getAxiosConfig(token)
    );
    return response.data;
  }

  // ============ Helper Functions ============

  /**
   * Get priority badge color for UI
   */
  getPriorityColor(priority: string): string {
    const priorityColors: Record<string, string> = {
      urgent: 'error',
      high: 'warning',
      normal: 'info',
      low: 'default'
    };
    return priorityColors[priority] || 'default';
  }
}

// Export singleton instance
export const planRequestService = new PlanRequestService();